import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, type AccrualView, type Company, type CostDocument, type NewAccrual } from '../api';
import type { Dictionary, Locale } from '../i18n';
import { amount, money, periodShort } from '../format';
import * as Icon from './Icons';
import { ErrorAlert, failureOf, type Failure } from './Alerts';
import type { Panel } from './Shell';

const monthOf = (value: string) => value.slice(0, 7);
const previousMonth = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1)).toISOString().slice(0, 7);
};

/** Domyślny koniec harmonogramu: rok od pierwszego miesiąca, bo tyle trwa polisa i abonament. */
function yearFrom(period: string): string {
  const [year, month] = period.split('-').map(Number);
  const end = new Date(Date.UTC(year, month - 1 + 11, 1));
  return end.toISOString().slice(0, 7);
}

type Form = NewAccrual & { costDocumentId: string };

/**
 * Rozliczenia międzyokresowe kosztów czynne (E7-S5).
 *
 * Panel stoi przy kosztach, a nie w osobnym miejscu w nawigacji: harmonogram zakłada się **na
 * dokumencie**, więc decyzja „to jest polisa na rok" pada tam, gdzie się ten dokument widzi.
 *
 * Harmonogramu nie liczymy w przeglądarce. Raty przychodzą z serwera razem z numerem zapisu, którym
 * weszły do dziennika — inaczej ekran pokazywałby swój własny rachunek obok tego, który jest
 * w księgach, a przy pierwszym zaokrągleniu te dwa przestałyby się zgadzać.
 */
export function CostAccrualsPanel({
  company,
  t,
  locale,
  editable,
  onGo,
}: {
  company: Company;
  t: Dictionary;
  locale: Locale;
  editable: boolean;
  onGo?: (panel: Panel) => void;
}) {
  const [rows, setRows] = useState<AccrualView[] | null>(null);
  const [documents, setDocuments] = useState<CostDocument[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [month, setMonth] = useState(previousMonth);
  const [message, setMessage] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<{ documentNumber: string; reason: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Failure | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [accruals, costs] = await Promise.all([api.accruals(company.id), api.costs(company.id)]);
      setRows(accruals);
      setDocuments(costs.filter((document) => document.status === 'BOOKED'));
    } catch (failure) {
      setError(failureOf(failure));
    }
  }, [company.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (failure) {
      setError(failureOf(failure));
    } finally {
      setBusy(false);
    }
  }

  /* Dokument, który już ma harmonogram, nie może dostać drugiego — nie ma go po co proponować */
  const available = useMemo(() => {
    const taken = new Set((rows ?? []).map((row) => row.accrual.costDocumentId));
    return documents.filter((document) => !taken.has(document.id));
  }, [documents, rows]);

  function start() {
    const document = available[0];
    const from = document ? monthOf(document.costDate) : previousMonth();
    setForm({
      costDocumentId: document?.id ?? '',
      description: '',
      totalAmount: '',
      fromPeriod: from,
      toPeriod: yearFrom(from),
    });
  }

  if (form) {
    const chosen = documents.find((document) => document.id === form.costDocumentId);
    return (
      <div className="panel">
        <header>
          <h4>{t.accruals.add}</h4>
          <div className="sp">
            <button type="button" className="btn ghost sm" onClick={() => setForm(null)}>
              {t.accruals.cancel}
            </button>
          </div>
        </header>
        <ErrorAlert error={error} t={t} onGo={onGo} />
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await api.addAccrual(company.id, {
                ...form,
                description: form.description?.trim() || null,
                totalAmount: form.totalAmount?.trim() || null,
                costAccount: form.costAccount?.trim() || null,
                accrualAccount: form.accrualAccount?.trim() || null,
              });
              setForm(null);
            });
          }}
        >
          <div className="fields">
            <div className="field">
              <label htmlFor="acDoc">{t.accruals.document}</label>
              <select
                id="acDoc"
                className="inp"
                value={form.costDocumentId}
                onChange={(event) => {
                  const document = documents.find((row) => row.id === event.target.value);
                  const from = document ? monthOf(document.costDate) : form.fromPeriod;
                  setForm({ ...form, costDocumentId: event.target.value, fromPeriod: from, toPeriod: yearFrom(from) });
                }}
                required
              >
                <option value="">—</option>
                {available.map((document) => (
                  <option key={document.id} value={document.id}>
                    {document.documentNumber} · {amount(document.costAmount, locale)} ·{' '}
                    {document.description ?? ''}
                  </option>
                ))}
              </select>
              <p className="hint">{t.accruals.documentHint}</p>
            </div>
            <div className="field">
              <label htmlFor="acDesc">{t.accruals.description}</label>
              <input
                id="acDesc"
                className="inp"
                value={form.description ?? ''}
                placeholder={chosen?.description ?? chosen?.documentNumber ?? ''}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="acAmount">{t.accruals.amount}</label>
              <input
                id="acAmount"
                className="inp n"
                inputMode="decimal"
                value={form.totalAmount ?? ''}
                placeholder={chosen ? chosen.costAmount : ''}
                onChange={(event) => setForm({ ...form, totalAmount: event.target.value })}
              />
              <p className="hint">{t.accruals.amountHint}</p>
            </div>
            <div className="field">
              <label htmlFor="acFrom">{t.accruals.from}</label>
              <input
                id="acFrom"
                className="inp"
                type="month"
                value={form.fromPeriod}
                onChange={(event) => setForm({ ...form, fromPeriod: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="acTo">{t.accruals.to}</label>
              <input
                id="acTo"
                className="inp"
                type="month"
                value={form.toPeriod}
                onChange={(event) => setForm({ ...form, toPeriod: event.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="acCost">{t.accruals.costAccount}</label>
              <input
                id="acCost"
                className="inp mono"
                value={form.costAccount ?? ''}
                onChange={(event) => setForm({ ...form, costAccount: event.target.value })}
              />
              <p className="hint">{t.accruals.accountsHint}</p>
            </div>
            <div className="field">
              <label htmlFor="acAccrual">{t.accruals.accrualAccount}</label>
              <input
                id="acAccrual"
                className="inp mono"
                value={form.accrualAccount ?? ''}
                onChange={(event) => setForm({ ...form, accrualAccount: event.target.value })}
              />
            </div>
          </div>
          <div className="row">
            <button type="submit" className="btn pri" disabled={busy || !form.costDocumentId}>
              {t.accruals.save}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="panel">
      <header>
        <h4>{t.accruals.title}</h4>
        <div className="sp">
          {editable && (
            <>
              <input
                className="inp sm"
                type="month"
                aria-label={t.accruals.month}
                value={month}
                onChange={(event) => setMonth(event.target.value)}
              />
              <button
                type="button"
                className="btn ghost sm"
                disabled={busy || !month}
                onClick={() =>
                  void run(async () => {
                    const result = await api.releaseAccruals(company.id, month);
                    setMessage(
                      `${t.accruals.released}: ${result.booked} · ${money(result.total, locale)} (${periodShort(result.period)})`,
                    );
                    setSkipped(result.skipped);
                  })
                }
              >
                {t.accruals.release}
              </button>
              <button type="button" className="btn sm" disabled={busy || available.length === 0} onClick={start}>
                <Icon.Plus />
                {t.accruals.add}
              </button>
            </>
          )}
        </div>
      </header>

      <p className="sm muted">{t.accruals.lead}</p>
      <ErrorAlert error={error} t={t} onGo={onGo} />
      {message && (
        <div className="alert a-ok">
          <Icon.Check />
          <div>
            <b>{message}</b>
          </div>
        </div>
      )}
      {/* Pominięte harmonogramy wymieniamy z powodu: cisza znaczyłaby „wszystko zaksięgowane" */}
      {skipped.length > 0 && (
        <div className="alert a-warn">
          <Icon.Warn />
          <div>
            <b>{t.accruals.skipped}</b>
            <ul>
              {skipped.map((row) => (
                <li key={`${row.documentNumber}-${row.reason}`}>
                  {row.documentNumber}: {row.reason}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {rows?.length === 0 ? (
        <div className="empty">
          <Icon.Calendar />
          <b>{t.accruals.none}</b>
          <p>{t.accruals.noneHint}</p>
        </div>
      ) : (
        rows && (
          <div className="tbl">
            <table>
              <thead>
                <tr>
                  <th>{t.accruals.document}</th>
                  <th>{t.accruals.description}</th>
                  <th className="n">{t.accruals.amount}</th>
                  <th>{t.accruals.period}</th>
                  <th className="n">{t.accruals.monthly}</th>
                  <th className="n">{t.accruals.releasedTotal}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.accrual.id}>
                    <td className="mono">{row.accrual.documentNumber}</td>
                    <td>{row.accrual.description}</td>
                    <td className="n">{amount(row.accrual.totalAmount, locale)}</td>
                    <td className="doc">
                      {periodShort(row.accrual.fromPeriod)}–{periodShort(row.accrual.toPeriod)}
                    </td>
                    <td className="n">{amount(row.plan[0]?.amount, locale)}</td>
                    <td className="n">
                      {amount(row.releasedTotal, locale)}
                      {row.releasedThrough && (
                        <span className="sm muted">
                          {' '}
                          {t.accruals.through} {periodShort(row.releasedThrough)}
                        </span>
                      )}
                    </td>
                    <td className="act">
                      <div className="row">
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() => setOpen(open === row.accrual.id ? null : row.accrual.id)}
                        >
                          {open === row.accrual.id ? t.accruals.hidePlan : t.accruals.showPlan}
                        </button>
                        {editable && row.releasedThrough === null && (
                          <button
                            type="button"
                            className="btn danger ico sm"
                            aria-label={t.accruals.remove}
                            title={t.accruals.remove}
                            disabled={busy}
                            onClick={() => {
                              const reason = window.prompt(t.accruals.removeReason);
                              if (reason) void run(() => api.removeAccrual(company.id, row.accrual.id, reason));
                            }}
                          >
                            <Icon.Trash />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {open && rows && (
        <div className="tbl">
          <table>
            <caption className="sm muted">
              {t.accruals.plan}: {rows.find((row) => row.accrual.id === open)?.accrual.description}
            </caption>
            <thead>
              <tr>
                <th>{t.accruals.month}</th>
                <th className="n">{t.accruals.instalment}</th>
                <th className="n">{t.accruals.remaining}</th>
                <th>{t.accruals.entry}</th>
              </tr>
            </thead>
            <tbody>
              {rows
                .find((row) => row.accrual.id === open)
                ?.plan.map((instalment) => (
                  <tr key={instalment.period}>
                    <td className="doc">{periodShort(instalment.period)}</td>
                    <td className="n">{amount(instalment.amount, locale)}</td>
                    <td className="n">{amount(instalment.remaining, locale)}</td>
                    <td>
                      {instalment.entryNo === null ? (
                        <span className="sm muted">{t.accruals.waiting}</span>
                      ) : (
                        <span className="mono">{instalment.entryNo}</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
